/**
 * GENERATED by the data-model pipeline (apps/data-model) — DO NOT EDIT
 * Source of truth: apps/data-model/schema/sql/
 * Regenerate: pnpm --filter data-model concat-schema && pnpm --filter data-model generate:all
 */

/**
 * Production parameter definitions, derived from the prefixed parameter
 * columns (@codegen:ts:strip-prefix) and the domains that type them.
 * Defaults, bounds, allowed values, units and descriptions come from the
 * domain declaration in schema/sql — do not hand-copy them elsewhere.
 */

export type ProductionParameterValueType = 'number' | 'boolean' | 'string' | 'uuid'

export type ProductionParameterOwnerTable =
  | 'concept'
  | 'fish_batch'
  | 'productioncycledepartment'
  | 'project'
  | 'project_setup_department'
  | 'sectiondepartment'

export type ProductionParameterOwner = {
  /** Owner table */
  table: ProductionParameterOwnerTable
  /** Column on the owner table (with its prefix) */
  column: string
}

export type ProductionParameterDefinition = {
  /** Parameter name as used from Zod onward */
  name: string
  type: ProductionParameterValueType
  nullable: boolean
  /** Domain default; null when the parameter has none */
  default: number | boolean | string | null
  min?: number
  max?: number
  /** Closed value set from the domain's CHECK (VALUE IN (...)) */
  allowed?: readonly string[]
  unit?: string
  step?: number
  description?: string
  /** Schema-qualified domain typing every owner column, when one exists */
  domain?: string
  owners: readonly ProductionParameterOwner[]
}

export const productionParameters = {
  fish_species: {
    name: 'fish_species',
    type: 'string',
    nullable: false,
    default: 'atlantic_salmon',
    allowed: ['atlantic_salmon', 'rainbow_trout', 'cod'],
    description:
      'Fish species used for this tank, determining the growth model, feed requirements, and water quality tolerances applied in calculations.',
    domain: 'main.prodparam_fish_species',
    owners: [
      { table: 'project', column: 'spp_fish_species' },
      { table: 'project_setup_department', column: 'spp_fish_species' },
      { table: 'fish_batch', column: 'pp_fish_species' },
      { table: 'sectiondepartment', column: 'pp_fish_species' }
    ]
  },
  fish_growth_table: {
    name: 'fish_growth_table',
    type: 'uuid',
    nullable: true,
    default: null,
    owners: [
      { table: 'project', column: 'spp_fish_growth_table' },
      { table: 'project_setup_department', column: 'spp_fish_growth_table' },
      { table: 'sectiondepartment', column: 'pp_fish_growth_table' }
    ]
  },
  fish_salinity_table: {
    name: 'fish_salinity_table',
    type: 'uuid',
    nullable: true,
    default: null,
    owners: [
      { table: 'project', column: 'spp_fish_salinity_table' },
      { table: 'project_setup_department', column: 'spp_fish_salinity_table' },
      { table: 'sectiondepartment', column: 'pp_fish_salinity_table' }
    ]
  },
  fish_relative_growth_index: {
    name: 'fish_relative_growth_index',
    type: 'number',
    nullable: false,
    default: 1,
    min: 0.1,
    max: 3,
    description:
      "Scales the species' growth curve up or down to reflect site-specific growth performance relative to the standard curve.",
    domain: 'main.prodparam_fish_relative_growth_index',
    owners: [
      { table: 'project', column: 'spp_fish_relative_growth_index' },
      { table: 'project_setup_department', column: 'spp_fish_relative_growth_index' },
      { table: 'sectiondepartment', column: 'pp_fish_relative_growth_index' },
      { table: 'productioncycledepartment', column: 'pp_fish_relative_growth_index' }
    ]
  },
  fish_density: {
    name: 'fish_density',
    type: 'number',
    nullable: false,
    default: 70,
    min: 10,
    max: 150,
    unit: 'kg/m³',
    step: 1,
    description:
      'Maximum biomass per cubic meter of tank volume allowed before density constrains stocking.',
    domain: 'main.prodparam_fish_density',
    owners: [
      { table: 'project', column: 'spp_fish_density' },
      { table: 'project_setup_department', column: 'spp_fish_density' },
      { table: 'sectiondepartment', column: 'pp_fish_density' }
    ]
  },
  fish_loss: {
    name: 'fish_loss',
    type: 'number',
    nullable: false,
    default: 0,
    min: 0,
    max: 100,
    unit: '%',
    description:
      'Expected daily mortality rate, applied to reduce the fish population over the production cycle.',
    domain: 'main.prodparam_fish_loss',
    owners: [
      { table: 'project', column: 'spp_fish_loss' },
      { table: 'project_setup_department', column: 'spp_fish_loss' },
      { table: 'sectiondepartment', column: 'pp_fish_loss' },
      { table: 'productioncycledepartment', column: 'pp_fish_loss' }
    ]
  },
  fish_o2_co2_conversion_factor: {
    name: 'fish_o2_co2_conversion_factor',
    type: 'number',
    nullable: false,
    default: 1.1,
    min: 0.1,
    max: 1.5,
    step: 0.1,
    description: "Factor used to derive CO2 production from the fish's oxygen consumption.",
    domain: 'main.prodparam_fish_o2_co2_conversion_factor',
    owners: [
      { table: 'project', column: 'spp_fish_o2_co2_conversion_factor' },
      { table: 'project_setup_department', column: 'spp_fish_o2_co2_conversion_factor' },
      { table: 'sectiondepartment', column: 'pp_fish_o2_co2_conversion_factor' }
    ]
  },
  fish_o2_adjustment_parameter: {
    name: 'fish_o2_adjustment_parameter',
    type: 'number',
    nullable: false,
    default: 150,
    min: 1,
    max: 500,
    unit: '%',
    step: 1,
    description:
      'Adjusts the standard oxygen consumption model to reflect species- or site-specific metabolic behavior.',
    domain: 'main.prodparam_fish_o2_adjustment_parameter',
    owners: [
      { table: 'project', column: 'spp_fish_o2_adjustment_parameter' },
      { table: 'project_setup_department', column: 'spp_fish_o2_adjustment_parameter' },
      { table: 'sectiondepartment', column: 'pp_fish_o2_adjustment_parameter' }
    ]
  },
  fish_suspended_solids_factor: {
    name: 'fish_suspended_solids_factor',
    type: 'number',
    nullable: false,
    default: 0.25,
    description: 'Factor used to estimate suspended solids produced per unit of feed consumed.',
    domain: 'main.prodparam_fish_suspended_solids_factor',
    owners: [
      { table: 'project', column: 'spp_fish_suspended_solids_factor' },
      { table: 'project_setup_department', column: 'spp_fish_suspended_solids_factor' },
      { table: 'sectiondepartment', column: 'pp_fish_suspended_solids_factor' }
    ]
  },
  fish_feed_conversion_ratio: {
    name: 'fish_feed_conversion_ratio',
    type: 'number',
    nullable: false,
    default: 1,
    min: 0.4,
    max: 1.5,
    unit: 'g/g',
    description: 'Amount of feed required per unit of fish weight gained (feed/growth).',
    domain: 'main.prodparam_fish_feed_conversion_ratio',
    owners: [
      { table: 'project', column: 'spp_fish_feed_conversion_ratio' },
      { table: 'project_setup_department', column: 'spp_fish_feed_conversion_ratio' },
      { table: 'sectiondepartment', column: 'pp_fish_feed_conversion_ratio' },
      { table: 'productioncycledepartment', column: 'pp_fish_feed_conversion_ratio' }
    ]
  },
  fish_tan_in_feed: {
    name: 'fish_tan_in_feed',
    type: 'number',
    nullable: false,
    default: 37,
    min: 1,
    max: 100,
    unit: 'g/kg',
    step: 1,
    description:
      'Total ammonia nitrogen (TAN) content of the feed, used to estimate TAN excretion from feeding.',
    domain: 'main.prodparam_fish_tan_in_feed',
    owners: [
      { table: 'project', column: 'spp_fish_tan_in_feed' },
      { table: 'project_setup_department', column: 'spp_fish_tan_in_feed' },
      { table: 'sectiondepartment', column: 'pp_fish_tan_in_feed' }
    ]
  },
  water_temperature: {
    name: 'water_temperature',
    type: 'number',
    nullable: false,
    default: 12,
    min: 1,
    max: 17,
    unit: '°C',
    step: 1,
    description: 'Production water temperature used for fish growth.',
    domain: 'main.prodparam_water_temperature',
    owners: [
      { table: 'project', column: 'spp_water_temperature' },
      { table: 'project_setup_department', column: 'spp_water_temperature' },
      { table: 'sectiondepartment', column: 'pp_water_temperature' },
      { table: 'productioncycledepartment', column: 'pp_water_temperature' }
    ]
  },
  water_temperature_table: {
    name: 'water_temperature_table',
    type: 'uuid',
    nullable: true,
    default: null,
    owners: [
      { table: 'project', column: 'spp_water_temperature_table' },
      { table: 'project_setup_department', column: 'spp_water_temperature_table' },
      { table: 'sectiondepartment', column: 'pp_water_temperature_table' },
      { table: 'productioncycledepartment', column: 'pp_water_temperature_table' }
    ]
  },
  water_salinity: {
    name: 'water_salinity',
    type: 'number',
    nullable: false,
    default: 0,
    min: 0,
    max: 40,
    unit: 'g/L',
    description: 'Salinity of the production water.',
    domain: 'main.prodparam_water_salinity',
    owners: [
      { table: 'project', column: 'spp_water_salinity' },
      { table: 'project_setup_department', column: 'spp_water_salinity' },
      { table: 'sectiondepartment', column: 'pp_water_salinity' }
    ]
  },
  water_saturation_o2: {
    name: 'water_saturation_o2',
    type: 'number',
    nullable: false,
    default: 80,
    min: 1,
    max: 100,
    unit: '%',
    step: 1,
    description:
      'Target oxygen saturation of the water leaving the tank, used to size oxygenation equipment.',
    domain: 'main.prodparam_water_saturation_o2',
    owners: [
      { table: 'project', column: 'spp_water_saturation_o2' },
      { table: 'project_setup_department', column: 'spp_water_saturation_o2' },
      { table: 'sectiondepartment', column: 'pp_water_saturation_o2' }
    ]
  },
  water_ph: {
    name: 'water_ph',
    type: 'number',
    nullable: false,
    default: 7,
    min: 1,
    max: 14,
    step: 1,
    description: 'pH of the water inside the tank.',
    domain: 'main.prodparam_water_ph',
    owners: [
      { table: 'project', column: 'spp_water_ph' },
      { table: 'project_setup_department', column: 'spp_water_ph' },
      { table: 'sectiondepartment', column: 'pp_water_ph' }
    ]
  },
  water_concentration_max_co2: {
    name: 'water_concentration_max_co2',
    type: 'number',
    nullable: false,
    default: 12,
    min: 0.1,
    max: 30,
    unit: 'mg/L',
    step: 1,
    description:
      'Maximum allowed CO2 concentration in the tank water before it affects fish welfare.',
    domain: 'main.prodparam_water_concentration_max_co2',
    owners: [
      { table: 'project', column: 'spp_water_concentration_max_co2' },
      { table: 'project_setup_department', column: 'spp_water_concentration_max_co2' },
      { table: 'sectiondepartment', column: 'pp_water_concentration_max_co2' }
    ]
  },
  water_concentration_max_tan: {
    name: 'water_concentration_max_tan',
    type: 'number',
    nullable: false,
    default: 4,
    min: 0.1,
    max: 50,
    unit: 'mg/L',
    step: 1,
    description: 'Maximum allowed total ammonia nitrogen (TAN) concentration in the tank water.',
    domain: 'main.prodparam_water_concentration_max_tan',
    owners: [
      { table: 'project', column: 'spp_water_concentration_max_tan' },
      { table: 'project_setup_department', column: 'spp_water_concentration_max_tan' },
      { table: 'sectiondepartment', column: 'pp_water_concentration_max_tan' }
    ]
  },
  water_concentration_max_nitrate: {
    name: 'water_concentration_max_nitrate',
    type: 'number',
    nullable: false,
    default: 150,
    min: 1,
    max: 250,
    unit: 'mg/L',
    step: 1,
    description: 'Maximum allowed nitrate concentration in the tank water.',
    domain: 'main.prodparam_water_concentration_max_nitrate',
    owners: [
      { table: 'project', column: 'spp_water_concentration_max_nitrate' },
      { table: 'project_setup_department', column: 'spp_water_concentration_max_nitrate' },
      { table: 'sectiondepartment', column: 'pp_water_concentration_max_nitrate' }
    ]
  },
  water_concentration_max_suspended_solids: {
    name: 'water_concentration_max_suspended_solids',
    type: 'number',
    nullable: false,
    default: 25,
    min: 1,
    max: 250,
    unit: 'mg/L',
    step: 1,
    description: 'Maximum allowed suspended solids concentration in the tank water.',
    domain: 'main.prodparam_water_concentration_max_suspended_solids',
    owners: [
      { table: 'project', column: 'spp_water_concentration_max_suspended_solids' },
      { table: 'project_setup_department', column: 'spp_water_concentration_max_suspended_solids' },
      { table: 'sectiondepartment', column: 'pp_water_concentration_max_suspended_solids' }
    ]
  },
  water_recirculation_forced_flow: {
    name: 'water_recirculation_forced_flow',
    type: 'number',
    nullable: false,
    default: 0,
    unit: 'L/min',
    description:
      'Recirculated water flow forced through the tank per minute, independent of the calculated requirement.',
    domain: 'main.prodparam_water_recirculation_forced_flow',
    owners: [
      { table: 'project', column: 'spp_water_recirculation_forced_flow' },
      { table: 'project_setup_department', column: 'spp_water_recirculation_forced_flow' },
      { table: 'sectiondepartment', column: 'pp_water_recirculation_forced_flow' }
    ]
  },
  water_makeup_min_flow: {
    name: 'water_makeup_min_flow',
    type: 'number',
    nullable: false,
    default: 0,
    unit: 'L/min',
    description:
      'Minimum makeup water flow supplied to the tank per minute, regardless of the calculated requirement.',
    domain: 'main.prodparam_water_makeup_min_flow',
    owners: [
      { table: 'project', column: 'spp_water_makeup_min_flow' },
      { table: 'project_setup_department', column: 'spp_water_makeup_min_flow' },
      { table: 'sectiondepartment', column: 'pp_water_makeup_min_flow' }
    ]
  },
  water_makeup_max_flow: {
    name: 'water_makeup_max_flow',
    type: 'number',
    nullable: false,
    default: 0,
    unit: 'L/min',
    description:
      'Maximum makeup water flow allowed into the tank per minute, capping the calculated requirement.',
    domain: 'main.prodparam_water_makeup_max_flow',
    owners: [
      { table: 'project', column: 'spp_water_makeup_max_flow' },
      { table: 'project_setup_department', column: 'spp_water_makeup_max_flow' },
      { table: 'sectiondepartment', column: 'pp_water_makeup_max_flow' }
    ]
  },
  technology_o2_enabled: {
    name: 'technology_o2_enabled',
    type: 'boolean',
    nullable: false,
    default: true,
    description:
      'Whether the oxygenation technology is installed on the recirculated water stream.',
    domain: 'main.prodparam_technology_o2_enabled',
    owners: [
      { table: 'project', column: 'spp_technology_o2_enabled' },
      { table: 'project_setup_department', column: 'spp_technology_o2_enabled' },
      { table: 'sectiondepartment', column: 'pp_technology_o2_enabled' }
    ]
  },
  technology_co2_enabled: {
    name: 'technology_co2_enabled',
    type: 'boolean',
    nullable: false,
    default: true,
    description:
      'Whether the co2 removal technology is installed on the recirculated water stream.',
    domain: 'main.prodparam_technology_co2_enabled',
    owners: [
      { table: 'project', column: 'spp_technology_co2_enabled' },
      { table: 'project_setup_department', column: 'spp_technology_co2_enabled' },
      { table: 'sectiondepartment', column: 'pp_technology_co2_enabled' }
    ]
  },
  technology_tan_enabled: {
    name: 'technology_tan_enabled',
    type: 'boolean',
    nullable: false,
    default: false,
    description:
      'Whether the tan removal technology is installed on the recirculated water stream.',
    domain: 'main.prodparam_technology_tan_enabled',
    owners: [
      { table: 'project', column: 'spp_technology_tan_enabled' },
      { table: 'project_setup_department', column: 'spp_technology_tan_enabled' },
      { table: 'sectiondepartment', column: 'pp_technology_tan_enabled' }
    ]
  },
  technology_nitrate_enabled: {
    name: 'technology_nitrate_enabled',
    type: 'boolean',
    nullable: false,
    default: false,
    description:
      'Whether the nitrate removal technology is installed on the recirculated water stream.',
    domain: 'main.prodparam_technology_nitrate_enabled',
    owners: [
      { table: 'project', column: 'spp_technology_nitrate_enabled' },
      { table: 'project_setup_department', column: 'spp_technology_nitrate_enabled' },
      { table: 'sectiondepartment', column: 'pp_technology_nitrate_enabled' }
    ]
  },
  technology_suspended_solids_enabled: {
    name: 'technology_suspended_solids_enabled',
    type: 'boolean',
    nullable: false,
    default: false,
    description:
      'Whether the suspended solids removal technology is installed on the recirculated water stream.',
    domain: 'main.prodparam_technology_suspended_solids_enabled',
    owners: [
      { table: 'project', column: 'spp_technology_suspended_solids_enabled' },
      { table: 'project_setup_department', column: 'spp_technology_suspended_solids_enabled' },
      { table: 'sectiondepartment', column: 'pp_technology_suspended_solids_enabled' }
    ]
  },
  technology_removal_efficiency_co2: {
    name: 'technology_removal_efficiency_co2',
    type: 'number',
    nullable: false,
    default: 75,
    min: 1,
    max: 100,
    unit: '%',
    step: 1,
    description:
      'Share of CO2 removed from the recirculated water by the CO2 treatment technology.',
    domain: 'main.prodparam_technology_removal_efficiency_co2',
    owners: [
      { table: 'project', column: 'spp_technology_removal_efficiency_co2' },
      { table: 'project_setup_department', column: 'spp_technology_removal_efficiency_co2' },
      { table: 'sectiondepartment', column: 'pp_technology_removal_efficiency_co2' }
    ]
  },
  technology_removal_efficiency_tan: {
    name: 'technology_removal_efficiency_tan',
    type: 'number',
    nullable: false,
    default: 50,
    min: 1,
    max: 100,
    unit: '%',
    step: 1,
    description:
      'Share of total ammonia nitrogen (TAN) removed from the recirculated water by the TAN treatment technology (e.g. biofilter).',
    domain: 'main.prodparam_technology_removal_efficiency_tan',
    owners: [
      { table: 'project', column: 'spp_technology_removal_efficiency_tan' },
      { table: 'project_setup_department', column: 'spp_technology_removal_efficiency_tan' },
      { table: 'sectiondepartment', column: 'pp_technology_removal_efficiency_tan' }
    ]
  },
  technology_removal_efficiency_nitrate: {
    name: 'technology_removal_efficiency_nitrate',
    type: 'number',
    nullable: false,
    default: 40,
    min: 1,
    max: 100,
    unit: '%',
    step: 1,
    description:
      'Share of nitrate removed from the recirculated water by the denitrification technology.',
    domain: 'main.prodparam_technology_removal_efficiency_nitrate',
    owners: [
      { table: 'project', column: 'spp_technology_removal_efficiency_nitrate' },
      { table: 'project_setup_department', column: 'spp_technology_removal_efficiency_nitrate' },
      { table: 'sectiondepartment', column: 'pp_technology_removal_efficiency_nitrate' }
    ]
  },
  technology_removal_efficiency_suspended_solids: {
    name: 'technology_removal_efficiency_suspended_solids',
    type: 'number',
    nullable: false,
    default: 70,
    min: 1,
    max: 100,
    unit: '%',
    step: 1,
    description:
      'Share of suspended solids removed from the recirculated water by the filtration technology.',
    domain: 'main.prodparam_technology_removal_efficiency_suspended_solids',
    owners: [
      { table: 'project', column: 'spp_technology_removal_efficiency_suspended_solids' },
      {
        table: 'project_setup_department',
        column: 'spp_technology_removal_efficiency_suspended_solids'
      },
      { table: 'sectiondepartment', column: 'pp_technology_removal_efficiency_suspended_solids' }
    ]
  },
  makeupwater_fresh_salinity: {
    name: 'makeupwater_fresh_salinity',
    type: 'number',
    nullable: false,
    default: 0,
    min: 0,
    max: 40,
    unit: 'g/L',
    step: 1,
    description: 'Salinity of the incoming freshwater used to dilute or replace tank water.',
    domain: 'main.prodparam_makeupwater_fresh_salinity',
    owners: [
      { table: 'project', column: 'spp_makeupwater_fresh_salinity' },
      { table: 'project_setup_department', column: 'spp_makeupwater_fresh_salinity' },
      { table: 'concept', column: 'pp_makeupwater_fresh_salinity' },
      { table: 'sectiondepartment', column: 'pp_makeupwater_fresh_salinity' }
    ]
  },
  makeupwater_fresh_limit: {
    name: 'makeupwater_fresh_limit',
    type: 'number',
    nullable: false,
    default: 0,
    unit: 'L/min',
    description: 'Maximum freshwater flow available per tank.',
    domain: 'main.prodparam_makeupwater_fresh_limit',
    owners: [
      { table: 'project', column: 'spp_makeupwater_fresh_limit' },
      { table: 'project_setup_department', column: 'spp_makeupwater_fresh_limit' },
      { table: 'concept', column: 'pp_makeupwater_fresh_limit' },
      { table: 'sectiondepartment', column: 'pp_makeupwater_fresh_limit' }
    ]
  },
  makeupwater_fresh_temperature: {
    name: 'makeupwater_fresh_temperature',
    type: 'number',
    nullable: false,
    default: 6,
    min: 0,
    max: 40,
    unit: '°C',
    step: 1,
    description: 'Temperature of the incoming freshwater.',
    domain: 'main.prodparam_makeupwater_fresh_temperature',
    owners: [
      { table: 'project', column: 'spp_makeupwater_fresh_temperature' },
      { table: 'project_setup_department', column: 'spp_makeupwater_fresh_temperature' },
      { table: 'concept', column: 'pp_makeupwater_fresh_temperature' },
      { table: 'sectiondepartment', column: 'pp_makeupwater_fresh_temperature' }
    ]
  },
  makeupwater_fresh_temperature_table: {
    name: 'makeupwater_fresh_temperature_table',
    type: 'uuid',
    nullable: true,
    default: null,
    owners: [
      { table: 'project', column: 'spp_makeupwater_fresh_temperature_table' },
      { table: 'project_setup_department', column: 'spp_makeupwater_fresh_temperature_table' },
      { table: 'sectiondepartment', column: 'pp_makeupwater_fresh_temperature_table' }
    ]
  },
  makeupwater_fresh_saturation_o2: {
    name: 'makeupwater_fresh_saturation_o2',
    type: 'number',
    nullable: false,
    default: 100,
    min: 0,
    max: 150,
    unit: '%',
    step: 1,
    description: 'Oxygen saturation of the incoming freshwater.',
    domain: 'main.prodparam_makeupwater_fresh_saturation_o2',
    owners: [
      { table: 'project', column: 'spp_makeupwater_fresh_saturation_o2' },
      { table: 'project_setup_department', column: 'spp_makeupwater_fresh_saturation_o2' },
      { table: 'concept', column: 'pp_makeupwater_fresh_saturation_o2' },
      { table: 'sectiondepartment', column: 'pp_makeupwater_fresh_saturation_o2' }
    ]
  },
  makeupwater_fresh_concentration_co2: {
    name: 'makeupwater_fresh_concentration_co2',
    type: 'number',
    nullable: false,
    default: 0,
    min: 0,
    max: 40,
    unit: 'mg/L',
    step: 1,
    description: 'CO2 concentration of the incoming freshwater.',
    domain: 'main.prodparam_makeupwater_fresh_concentration_co2',
    owners: [
      { table: 'project', column: 'spp_makeupwater_fresh_concentration_co2' },
      { table: 'project_setup_department', column: 'spp_makeupwater_fresh_concentration_co2' },
      { table: 'concept', column: 'pp_makeupwater_fresh_concentration_co2' },
      { table: 'sectiondepartment', column: 'pp_makeupwater_fresh_concentration_co2' }
    ]
  },
  makeupwater_fresh_concentration_tan: {
    name: 'makeupwater_fresh_concentration_tan',
    type: 'number',
    nullable: false,
    default: 0,
    min: 0,
    max: 40,
    unit: 'mg/L',
    step: 1,
    description: 'Total ammonia nitrogen (TAN) concentration of the incoming freshwater.',
    domain: 'main.prodparam_makeupwater_fresh_concentration_tan',
    owners: [
      { table: 'project', column: 'spp_makeupwater_fresh_concentration_tan' },
      { table: 'project_setup_department', column: 'spp_makeupwater_fresh_concentration_tan' },
      { table: 'concept', column: 'pp_makeupwater_fresh_concentration_tan' },
      { table: 'sectiondepartment', column: 'pp_makeupwater_fresh_concentration_tan' }
    ]
  },
  makeupwater_fresh_concentration_nitrate: {
    name: 'makeupwater_fresh_concentration_nitrate',
    type: 'number',
    nullable: false,
    default: 0,
    min: 0,
    max: 40,
    unit: 'mg/L',
    step: 1,
    description: 'Nitrate concentration of the incoming freshwater.',
    domain: 'main.prodparam_makeupwater_fresh_concentration_nitrate',
    owners: [
      { table: 'project', column: 'spp_makeupwater_fresh_concentration_nitrate' },
      { table: 'project_setup_department', column: 'spp_makeupwater_fresh_concentration_nitrate' },
      { table: 'concept', column: 'pp_makeupwater_fresh_concentration_nitrate' },
      { table: 'sectiondepartment', column: 'pp_makeupwater_fresh_concentration_nitrate' }
    ]
  },
  makeupwater_fresh_concentration_suspended_solids: {
    name: 'makeupwater_fresh_concentration_suspended_solids',
    type: 'number',
    nullable: false,
    default: 0,
    min: 0,
    max: 40,
    unit: 'mg/L',
    step: 1,
    description: 'Suspended solids concentration of the incoming freshwater.',
    domain: 'main.prodparam_makeupwater_fresh_concentration_suspended_solids',
    owners: [
      { table: 'project', column: 'spp_makeupwater_fresh_concentration_suspended_solids' },
      {
        table: 'project_setup_department',
        column: 'spp_makeupwater_fresh_concentration_suspended_solids'
      },
      { table: 'concept', column: 'pp_makeupwater_fresh_concentration_suspended_solids' },
      { table: 'sectiondepartment', column: 'pp_makeupwater_fresh_concentration_suspended_solids' }
    ]
  },
  makeupwater_fresh_alkalinity: {
    name: 'makeupwater_fresh_alkalinity',
    type: 'number',
    nullable: false,
    default: 100,
    min: 1,
    max: 3000,
    unit: 'μmol/kg',
    step: 1,
    description:
      'Alkalinity of the incoming freshwater, used to model water chemistry and buffering capacity.',
    domain: 'main.prodparam_makeupwater_fresh_alkalinity',
    owners: [
      { table: 'project', column: 'spp_makeupwater_fresh_alkalinity' },
      { table: 'project_setup_department', column: 'spp_makeupwater_fresh_alkalinity' },
      { table: 'concept', column: 'pp_makeupwater_fresh_alkalinity' },
      { table: 'sectiondepartment', column: 'pp_makeupwater_fresh_alkalinity' }
    ]
  },
  makeupwater_fresh_dissolved_inorganic_carbon: {
    name: 'makeupwater_fresh_dissolved_inorganic_carbon',
    type: 'number',
    nullable: false,
    default: 100,
    min: 1,
    max: 3000,
    unit: 'μmol/kg',
    step: 1,
    description:
      'Dissolved inorganic carbon concentration of the incoming freshwater, used to model water chemistry.',
    domain: 'main.prodparam_makeupwater_fresh_dissolved_inorganic_carbon',
    owners: [
      { table: 'project', column: 'spp_makeupwater_fresh_dissolved_inorganic_carbon' },
      {
        table: 'project_setup_department',
        column: 'spp_makeupwater_fresh_dissolved_inorganic_carbon'
      },
      { table: 'concept', column: 'pp_makeupwater_fresh_dissolved_inorganic_carbon' },
      { table: 'sectiondepartment', column: 'pp_makeupwater_fresh_dissolved_inorganic_carbon' }
    ]
  },
  makeupwater_fresh_ph: {
    name: 'makeupwater_fresh_ph',
    type: 'number',
    nullable: false,
    default: 7,
    min: 1,
    max: 14,
    step: 0.1,
    description: 'pH of the incoming freshwater.',
    domain: 'main.prodparam_makeupwater_fresh_ph',
    owners: [
      { table: 'project', column: 'spp_makeupwater_fresh_ph' },
      { table: 'project_setup_department', column: 'spp_makeupwater_fresh_ph' },
      { table: 'concept', column: 'pp_makeupwater_fresh_ph' },
      { table: 'sectiondepartment', column: 'pp_makeupwater_fresh_ph' }
    ]
  },
  makeupwater_sea_salinity: {
    name: 'makeupwater_sea_salinity',
    type: 'number',
    nullable: false,
    default: 34,
    min: 0,
    max: 40,
    unit: 'g/L',
    step: 1,
    description: 'Salinity of the incoming seawater used to dilute or replace tank water.',
    domain: 'main.prodparam_makeupwater_sea_salinity',
    owners: [
      { table: 'project', column: 'spp_makeupwater_sea_salinity' },
      { table: 'project_setup_department', column: 'spp_makeupwater_sea_salinity' },
      { table: 'concept', column: 'pp_makeupwater_sea_salinity' },
      { table: 'sectiondepartment', column: 'pp_makeupwater_sea_salinity' }
    ]
  },
  makeupwater_sea_limit: {
    name: 'makeupwater_sea_limit',
    type: 'number',
    nullable: false,
    default: 0,
    unit: 'L/min',
    description: 'Maximum seawater flow available per tank.',
    domain: 'main.prodparam_makeupwater_sea_limit',
    owners: [
      { table: 'project', column: 'spp_makeupwater_sea_limit' },
      { table: 'project_setup_department', column: 'spp_makeupwater_sea_limit' },
      { table: 'concept', column: 'pp_makeupwater_sea_limit' },
      { table: 'sectiondepartment', column: 'pp_makeupwater_sea_limit' }
    ]
  },
  makeupwater_sea_temperature: {
    name: 'makeupwater_sea_temperature',
    type: 'number',
    nullable: false,
    default: 10,
    min: 0,
    max: 40,
    unit: '°C',
    step: 1,
    description: 'Temperature of the incoming seawater.',
    domain: 'main.prodparam_makeupwater_sea_temperature',
    owners: [
      { table: 'project', column: 'spp_makeupwater_sea_temperature' },
      { table: 'project_setup_department', column: 'spp_makeupwater_sea_temperature' },
      { table: 'concept', column: 'pp_makeupwater_sea_temperature' },
      { table: 'sectiondepartment', column: 'pp_makeupwater_sea_temperature' }
    ]
  },
  makeupwater_sea_temperature_table: {
    name: 'makeupwater_sea_temperature_table',
    type: 'uuid',
    nullable: true,
    default: null,
    owners: [
      { table: 'project', column: 'spp_makeupwater_sea_temperature_table' },
      { table: 'project_setup_department', column: 'spp_makeupwater_sea_temperature_table' },
      { table: 'sectiondepartment', column: 'pp_makeupwater_sea_temperature_table' }
    ]
  },
  makeupwater_sea_saturation_o2: {
    name: 'makeupwater_sea_saturation_o2',
    type: 'number',
    nullable: false,
    default: 100,
    min: 0,
    max: 150,
    unit: '%',
    step: 1,
    description: 'Oxygen saturation of the incoming seawater.',
    domain: 'main.prodparam_makeupwater_sea_saturation_o2',
    owners: [
      { table: 'project', column: 'spp_makeupwater_sea_saturation_o2' },
      { table: 'project_setup_department', column: 'spp_makeupwater_sea_saturation_o2' },
      { table: 'concept', column: 'pp_makeupwater_sea_saturation_o2' },
      { table: 'sectiondepartment', column: 'pp_makeupwater_sea_saturation_o2' }
    ]
  },
  makeupwater_sea_concentration_co2: {
    name: 'makeupwater_sea_concentration_co2',
    type: 'number',
    nullable: false,
    default: 0,
    min: 0,
    max: 40,
    unit: 'mg/L',
    step: 1,
    description: 'CO2 concentration of the incoming seawater.',
    domain: 'main.prodparam_makeupwater_sea_concentration_co2',
    owners: [
      { table: 'project', column: 'spp_makeupwater_sea_concentration_co2' },
      { table: 'project_setup_department', column: 'spp_makeupwater_sea_concentration_co2' },
      { table: 'concept', column: 'pp_makeupwater_sea_concentration_co2' },
      { table: 'sectiondepartment', column: 'pp_makeupwater_sea_concentration_co2' }
    ]
  },
  makeupwater_sea_concentration_tan: {
    name: 'makeupwater_sea_concentration_tan',
    type: 'number',
    nullable: false,
    default: 0,
    min: 0,
    max: 40,
    unit: 'mg/L',
    step: 1,
    description: 'Total ammonia nitrogen (TAN) concentration of the incoming seawater.',
    domain: 'main.prodparam_makeupwater_sea_concentration_tan',
    owners: [
      { table: 'project', column: 'spp_makeupwater_sea_concentration_tan' },
      { table: 'project_setup_department', column: 'spp_makeupwater_sea_concentration_tan' },
      { table: 'concept', column: 'pp_makeupwater_sea_concentration_tan' },
      { table: 'sectiondepartment', column: 'pp_makeupwater_sea_concentration_tan' }
    ]
  },
  makeupwater_sea_concentration_nitrate: {
    name: 'makeupwater_sea_concentration_nitrate',
    type: 'number',
    nullable: false,
    default: 0,
    min: 0,
    max: 40,
    unit: 'mg/L',
    step: 1,
    description: 'Nitrate concentration of the incoming seawater.',
    domain: 'main.prodparam_makeupwater_sea_concentration_nitrate',
    owners: [
      { table: 'project', column: 'spp_makeupwater_sea_concentration_nitrate' },
      { table: 'project_setup_department', column: 'spp_makeupwater_sea_concentration_nitrate' },
      { table: 'concept', column: 'pp_makeupwater_sea_concentration_nitrate' },
      { table: 'sectiondepartment', column: 'pp_makeupwater_sea_concentration_nitrate' }
    ]
  },
  makeupwater_sea_concentration_suspended_solids: {
    name: 'makeupwater_sea_concentration_suspended_solids',
    type: 'number',
    nullable: false,
    default: 0,
    min: 0,
    max: 40,
    unit: 'mg/L',
    step: 1,
    description: 'Suspended solids concentration of the incoming seawater.',
    domain: 'main.prodparam_makeupwater_sea_concentration_suspended_solids',
    owners: [
      { table: 'project', column: 'spp_makeupwater_sea_concentration_suspended_solids' },
      {
        table: 'project_setup_department',
        column: 'spp_makeupwater_sea_concentration_suspended_solids'
      },
      { table: 'concept', column: 'pp_makeupwater_sea_concentration_suspended_solids' },
      { table: 'sectiondepartment', column: 'pp_makeupwater_sea_concentration_suspended_solids' }
    ]
  },
  makeupwater_sea_alkalinity: {
    name: 'makeupwater_sea_alkalinity',
    type: 'number',
    nullable: false,
    default: 2300,
    min: 1,
    max: 3000,
    unit: 'μmol/kg',
    step: 1,
    description:
      'Alkalinity of the incoming seawater, used to model water chemistry and buffering capacity.',
    domain: 'main.prodparam_makeupwater_sea_alkalinity',
    owners: [
      { table: 'project', column: 'spp_makeupwater_sea_alkalinity' },
      { table: 'project_setup_department', column: 'spp_makeupwater_sea_alkalinity' },
      { table: 'concept', column: 'pp_makeupwater_sea_alkalinity' },
      { table: 'sectiondepartment', column: 'pp_makeupwater_sea_alkalinity' }
    ]
  },
  makeupwater_sea_dissolved_inorganic_carbon: {
    name: 'makeupwater_sea_dissolved_inorganic_carbon',
    type: 'number',
    nullable: false,
    default: 2300,
    min: 1,
    max: 3000,
    unit: 'μmol/kg',
    step: 1,
    description:
      'Dissolved inorganic carbon concentration of the incoming seawater, used to model water chemistry.',
    domain: 'main.prodparam_makeupwater_sea_dissolved_inorganic_carbon',
    owners: [
      { table: 'project', column: 'spp_makeupwater_sea_dissolved_inorganic_carbon' },
      {
        table: 'project_setup_department',
        column: 'spp_makeupwater_sea_dissolved_inorganic_carbon'
      },
      { table: 'concept', column: 'pp_makeupwater_sea_dissolved_inorganic_carbon' },
      { table: 'sectiondepartment', column: 'pp_makeupwater_sea_dissolved_inorganic_carbon' }
    ]
  },
  makeupwater_sea_ph: {
    name: 'makeupwater_sea_ph',
    type: 'number',
    nullable: false,
    default: 8,
    min: 1,
    max: 14,
    step: 0.1,
    description: 'pH of the incoming seawater.',
    domain: 'main.prodparam_makeupwater_sea_ph',
    owners: [
      { table: 'project', column: 'spp_makeupwater_sea_ph' },
      { table: 'project_setup_department', column: 'spp_makeupwater_sea_ph' },
      { table: 'concept', column: 'pp_makeupwater_sea_ph' },
      { table: 'sectiondepartment', column: 'pp_makeupwater_sea_ph' }
    ]
  },
  fish_degree_days: {
    name: 'fish_degree_days',
    type: 'number',
    nullable: false,
    default: 500,
    min: 1,
    unit: '°D',
    step: 1,
    description:
      'Degree days (days × °C) a hatchery batch incubates for: the daily water temperature is summed until this target is reached, which sets the days in the hatchery. Unused outside a hatchery department.',
    domain: 'main.prodparam_fish_degree_days',
    owners: [{ table: 'productioncycledepartment', column: 'pp_fish_degree_days' }]
  }
} as const satisfies Record<string, ProductionParameterDefinition>

export type ProductionParameterName = keyof typeof productionParameters

export const productionParameterNames = Object.keys(
  productionParameters
) as ProductionParameterName[]
